// gloss:file

import { applyCaps } from '../shared/applyCaps';
import { compactRequest } from '../shared/compactRequest';
import { mergeKeys } from '../shared/mergeKeys';
import { readPage } from '../shared/readPage';
import { readPath } from '../shared/readPath';
import type { ByPageOptions, PageWalk, SdkItems, WalkedPage } from '../types';
import { IncompletePageWalkError } from './incompletePageWalkError';

const LABEL = 'iterateByPage';

// gloss
const isBeforeLastPage = (totalPages: unknown, items: unknown[], page: number): boolean =>
  items.length > 0 && typeof totalPages === 'number' && page < totalPages;

// gloss
const readTotal = (response: unknown, path: string, page: number): number => {
  const total = readPath(response, path);
  if (typeof total !== 'number') throw new IncompletePageWalkError(LABEL, `page ${page} has no total at ${path}`);
  return total;
};

// gloss
export const iterateByPage = <R, T = SdkItems<R>>(options: ByPageOptions<R, T>): PageWalk<WalkedPage<T>> => ({
  async *[Symbol.asyncIterator]() {
    const keys = mergeKeys('page', options.keys);
    const progress = { pages: 0, rows: 0 };
    const seenKeys = new Set<string>();
    let firstTotal: number | undefined;

    for (;;) {
      const request = compactRequest({ [keys.page]: progress.pages + 1, [keys.pageSize]: options.pageSize });
      const { response, items } = await readPage<R, T, typeof request>(options, request, progress, LABEL, keys.data);
      const page = progress.pages;
      if (options.itemKey) {
        const total = readTotal(response, keys.total, page);
        firstTotal ??= total;
        if (total !== firstTotal) {
          throw new IncompletePageWalkError(
            LABEL,
            `page ${page} reports a total of ${total}, page 1 reported ${firstTotal}`,
          );
        }
        for (const item of items) seenKeys.add(options.itemKey(item));
      }
      const isAnother = options.hasMore
        ? options.hasMore(response, items, page)
        : isBeforeLastPage(readPath(response, keys.totalPages), items, page);

      const isCapped = isAnother && applyCaps(options, progress);
      if (options.itemKey && !isAnother && seenKeys.size !== firstTotal) {
        throw new IncompletePageWalkError(
          LABEL,
          `the walk read ${seenKeys.size} distinct items, page 1 reported a total of ${firstTotal}`,
        );
      }
      yield { items, page };
      if (!isAnother || isCapped) return;
    }
  },
});
